import { useEffect, useRef, useState } from 'react';
import type {
  PhotoDiagnosisSeverity,
  PhotoRecord,
  UserRole,
} from '@greeniq/shared';
import { fileToBase64, listZonePhotos, uploadPhoto } from '../api/photos.js';
import { ApiError } from '../api/client.js';

interface Props {
  courseSlug: string;
  zoneId: number;
  role: UserRole | undefined;
}

const UPLOAD_ROLES: UserRole[] = ['ageye_admin', 'superintendent', 'assistant_super', 'crew'];

const SEVERITY_COLOR: Record<PhotoDiagnosisSeverity, string> = {
  low: '#15803d',
  medium: '#d97706',
  high: '#b91c1c',
};

export function PhotoLogCard({ courseSlug, zoneId, role }: Props) {
  const canUpload = role ? UPLOAD_ROLES.includes(role) : false;
  const [photos, setPhotos] = useState<PhotoRecord[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);

  async function load() {
    try {
      const resp = await listZonePhotos(courseSlug, zoneId);
      setPhotos(resp.photos);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  useEffect(() => {
    void load();
  }, [courseSlug, zoneId]);

  async function onUpload(file: File) {
    setUploading(true);
    setError(null);
    try {
      const encoded = await fileToBase64(file);
      await uploadPhoto(courseSlug, zoneId, {
        mimeType: encoded.mimeType,
        base64: encoded.base64,
        originalFilename: encoded.name,
        caption: caption || undefined,
        diagnose: true,
      });
      setCaption('');
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  }

  return (
    <section className="mt-6 p-5 bg-bg-card rounded-lg border border-border">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm uppercase tracking-wider text-fg-muted font-semibold">
            Photo log
          </h2>
          <p className="mt-1 text-xs text-fg-muted">
            {photos ? `${photos.length} photo${photos.length === 1 ? '' : 's'} on this zone` : ''}
          </p>
        </div>
        {canUpload && (
          <div className="flex items-center gap-2 flex-wrap">
            <input
              ref={fileRef}
              type="file"
              accept="image/*,.heic,.heif"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onUpload(f);
              }}
              className="text-xs"
              disabled={uploading}
            />
          </div>
        )}
      </div>

      {canUpload && (
        <div className="mt-3">
          <input
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Optional caption — what are you seeing?"
            maxLength={500}
            className="w-full text-sm border border-border rounded-md px-3 py-2"
            disabled={uploading}
          />
        </div>
      )}

      {error && (
        <div className="mt-3 text-xs px-3 py-2 rounded-md bg-error-muted text-[color:var(--color-error)]">
          {error}
        </div>
      )}

      {uploading && (
        <div className="mt-3 text-xs text-fg-muted">
          Uploading + running diagnosis…
        </div>
      )}

      {photos && photos.length === 0 && !uploading && (
        <p className="mt-4 text-sm text-fg-muted">
          {canUpload
            ? 'No photos yet. Drop a photo above and Claude will take a first pass at identifying what it sees.'
            : 'No photos yet on this zone.'}
        </p>
      )}

      {photos && photos.length > 0 && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {photos.map((p) => (
            <PhotoRow key={p.id} photo={p} />
          ))}
        </div>
      )}
    </section>
  );
}

function PhotoRow({ photo }: { photo: PhotoRecord }) {
  const imgSrc = `/api/photos/public/${photo.publicToken}`;
  const severity = (photo.severity as PhotoDiagnosisSeverity | null) ?? null;
  const sevColor = severity ? SEVERITY_COLOR[severity] : '#9ca3af';
  const confidence = photo.confidence !== null && photo.confidence !== undefined
    ? Number(photo.confidence)
    : null;

  return (
    <article className="rounded-md overflow-hidden bg-bg-elevated border border-border">
      <img
        src={imgSrc}
        alt={photo.caption ?? photo.issueLabel ?? 'Zone photo'}
        className="w-full aspect-[4/3] object-cover"
        loading="lazy"
      />
      <div className="p-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[10px] text-fg-muted">
            {new Date(photo.createdAt).toLocaleString()}
          </div>
          {photo.issueLabel && (
            <span
              className="text-[10px] px-2 py-0.5 rounded-full font-semibold text-white uppercase tracking-wider"
              style={{ backgroundColor: sevColor }}
            >
              {photo.issueLabel}
              {confidence !== null ? ` · ${Math.round(confidence * 100)}%` : ''}
            </span>
          )}
        </div>
        {photo.caption && (
          <p className="mt-2 text-sm text-fg-muted italic">"{photo.caption}"</p>
        )}
        {photo.summary && (
          <p className="mt-2 text-sm text-fg">{photo.summary}</p>
        )}
        {photo.recommendedActions && photo.recommendedActions.length > 0 && (
          <ul className="mt-2 text-sm text-greeniq-300 list-disc list-inside space-y-0.5">
            {photo.recommendedActions.map((a, i) => (
              <li key={i} className="font-semibold">{a}</li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}
